import { CSSProperties } from 'react';

interface DoorVisualProps {
  panelType: string;
  colorHex: string;
  isLight: boolean;
  glassId: string;
}

function GlassPane({ glassId, style }: { glassId: string; style?: CSSProperties }) {
  const base: CSSProperties = {
    width: '100%',
    height: '100%',
    borderRadius: '2px',
    ...style,
  };

  if (glassId === 'solid') {
    return null;
  }
  if (glassId === 'frosted') {
    return (
      <div
        style={{
          ...base,
          background:
            'linear-gradient(135deg, rgba(240,242,245,0.75) 0%, rgba(225,230,240,0.65) 100%)',
          border: '1px solid rgba(255,255,255,0.5)',
        }}
      />
    );
  }
  if (glassId === 'diamond') {
    return (
      <div
        style={{
          ...base,
          background: 'rgba(210,230,248,0.45)',
          border: '1px solid rgba(255,255,255,0.45)',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundImage:
              'linear-gradient(45deg, rgba(140,190,220,0.3) 25%, transparent 25%, transparent 75%, rgba(140,190,220,0.3) 75%), linear-gradient(45deg, rgba(140,190,220,0.3) 25%, transparent 25%, transparent 75%, rgba(140,190,220,0.3) 75%)',
            backgroundSize: '14px 14px',
            backgroundPosition: '0 0, 7px 7px',
          }}
        />
      </div>
    );
  }
  if (glassId === 'camelot') {
    return (
      <div
        style={{
          ...base,
          background:
            'linear-gradient(to bottom, rgba(220,195,150,0.45) 0%, rgba(210,230,248,0.5) 45%, rgba(210,230,248,0.5) 100%)',
          border: '1px solid rgba(255,255,255,0.4)',
        }}
      />
    );
  }
  if (glassId === 'zinc') {
    return (
      <div
        style={{
          ...base,
          background: 'rgba(210,228,218,0.45)',
          border: '1px solid rgba(255,255,255,0.45)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(45deg)',
            width: '55%',
            aspectRatio: '1',
            border: '2px solid rgba(180,200,185,0.6)',
            borderRadius: '2px',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '40%',
            aspectRatio: '1',
            border: '2px solid rgba(180,200,185,0.5)',
            borderRadius: '50%',
          }}
        />
      </div>
    );
  }
  // clear / default
  return (
    <div
      style={{
        ...base,
        background:
          'linear-gradient(135deg, rgba(200,228,252,0.5) 0%, rgba(220,240,255,0.35) 50%, rgba(195,222,248,0.5) 100%)',
        border: '1px solid rgba(255,255,255,0.5)',
      }}
    />
  );
}

export default function DoorVisual({ panelType, colorHex, isLight, glassId }: DoorVisualProps) {
  const highlight = isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.16)';
  const deepShadow = isLight ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.5)';
  const frameAlpha = isLight ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.55)';

  const raisedPanel: CSSProperties = {
    boxShadow: `inset 2px 2px 4px ${highlight}, inset -2px -2px 4px ${deepShadow}`,
    borderRadius: '3px',
    backgroundColor: colorHex,
  };

  const doorWrap: CSSProperties = {
    backgroundColor: colorHex,
    border: `3px solid ${frameAlpha}`,
    borderRadius: '3px',
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  };

  const hasGlass = glassId !== 'solid';

  const Handle = () => (
    <div
      style={{
        position: 'absolute',
        right: '14%',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '5px',
        height: '28%',
        background: 'linear-gradient(to right, #999, #dedede, #999)',
        borderRadius: '3px',
        zIndex: 10,
      }}
    />
  );

  const Letterbox = () => (
    <div
      style={{
        position: 'absolute',
        bottom: '25%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '32%',
        height: '5px',
        background: 'linear-gradient(to bottom, #888, #ccc, #888)',
        borderRadius: '2px',
        zIndex: 10,
      }}
    />
  );

  const renderLayout = () => {
    switch (panelType) {
      case 'single-panel':
        return (
          <div style={{ position: 'absolute', inset: '8%' }}>
            <div style={{ ...raisedPanel, width: '100%', height: '100%' }} />
          </div>
        );

      case 'two-panel':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'flex',
              flexDirection: 'column',
              gap: '7%',
            }}
          >
            {[0, 1].map((i) => (
              <div key={i} style={{ flex: 1, ...raisedPanel }} />
            ))}
          </div>
        );

      case 'three-panel':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'flex',
              flexDirection: 'column',
              gap: '5%',
            }}
          >
            {[0, 1, 2].map((i) => (
              <div key={i} style={{ flex: 1, ...raisedPanel }} />
            ))}
          </div>
        );

      case 'single-panel-glass':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'flex',
              flexDirection: 'column',
              gap: '6%',
            }}
          >
            <div
              style={{
                flex: '0 0 42%',
                borderRadius: '3px',
                overflow: 'hidden',
                position: 'relative',
                ...(hasGlass ? {} : raisedPanel),
              }}
            >
              {hasGlass && (
                <div style={{ position: 'absolute', inset: 0 }}>
                  <GlassPane glassId={glassId} />
                </div>
              )}
            </div>
            <div style={{ flex: 1, ...raisedPanel }} />
          </div>
        );

      case 'four-panel':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: '1fr 1fr',
              gap: '8%',
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={{ ...raisedPanel }} />
            ))}
          </div>
        );

      case 'six-panel':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: '1fr 1fr 1fr',
              gap: '5%',
            }}
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} style={{ ...raisedPanel }} />
            ))}
          </div>
        );

      case 'two-panel-top-glass':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'flex',
              flexDirection: 'column',
              gap: '7%',
            }}
          >
            <div
              style={{
                flex: '0 0 36%',
                borderRadius: '3px',
                overflow: 'hidden',
                position: 'relative',
                ...(hasGlass ? {} : raisedPanel),
              }}
            >
              {hasGlass && (
                <div style={{ position: 'absolute', inset: 0 }}>
                  <GlassPane glassId={glassId} />
                </div>
              )}
            </div>
            <div
              style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8%',
              }}
            >
              {[0, 1].map((i) => (
                <div key={i} style={{ ...raisedPanel }} />
              ))}
            </div>
          </div>
        );

      case 'four-panel-top-glass':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'flex',
              flexDirection: 'column',
              gap: '5%',
            }}
          >
            <div
              style={{
                flex: '0 0 24%',
                borderRadius: '3px',
                overflow: 'hidden',
                position: 'relative',
                ...(hasGlass ? {} : raisedPanel),
              }}
            >
              {hasGlass && (
                <div style={{ position: 'absolute', inset: 0 }}>
                  <GlassPane glassId={glassId} />
                </div>
              )}
            </div>
            <div
              style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridTemplateRows: '1fr 1fr',
                gap: '5%',
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <div key={i} style={{ ...raisedPanel }} />
              ))}
            </div>
          </div>
        );

      case 'cottage-glass':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'flex',
              flexDirection: 'column',
              gap: '6%',
            }}
          >
            <div
              style={{
                flex: '0 0 52%',
                borderRadius: '3px',
                overflow: 'hidden',
                position: 'relative',
                ...(hasGlass ? {} : raisedPanel),
              }}
            >
              {hasGlass && (
                <div style={{ position: 'absolute', inset: 0 }}>
                  <GlassPane glassId={glassId} />
                </div>
              )}
            </div>
            <div
              style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8%',
              }}
            >
              {[0, 1].map((i) => (
                <div key={i} style={{ ...raisedPanel }} />
              ))}
            </div>
          </div>
        );

      case 'stable':
        return (
          <>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '48%',
                borderBottom: `3px solid ${frameAlpha}`,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: '10%',
                  borderRadius: '3px',
                  overflow: 'hidden',
                  ...(hasGlass ? {} : raisedPanel),
                }}
              >
                {hasGlass && <GlassPane glassId={glassId} />}
              </div>
            </div>
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '52%',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: '10%',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '10%',
                }}
              >
                {[0, 1].map((i) => (
                  <div key={i} style={{ ...raisedPanel }} />
                ))}
              </div>
            </div>
          </>
        );

      case 'ledge-braced':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'flex',
              flexDirection: 'column',
              gap: '5%',
            }}
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  boxShadow: `inset 0 2px 4px ${highlight}, inset 0 -2px 4px ${deepShadow}`,
                  borderRadius: '2px',
                  backgroundColor: colorHex,
                }}
              />
            ))}
          </div>
        );

      case 'arched-glass':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'flex',
              flexDirection: 'column',
              gap: '6%',
            }}
          >
            <div
              style={{
                flex: '0 0 34%',
                borderRadius: '50% 50% 4px 4px / 38% 38% 4px 4px',
                overflow: 'hidden',
                position: 'relative',
                ...(hasGlass ? {} : raisedPanel),
              }}
            >
              {hasGlass && (
                <div style={{ position: 'absolute', inset: 0 }}>
                  <GlassPane glassId={glassId} />
                </div>
              )}
            </div>
            <div
              style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8%',
              }}
            >
              {[0, 1].map((i) => (
                <div key={i} style={{ ...raisedPanel }} />
              ))}
            </div>
          </div>
        );

      case 'flat':
        return null;

      case 'horizontal-bars':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8% 12%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
            }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  height: '14%',
                  boxShadow: `inset 0 3px 5px ${deepShadow}, inset 0 -1px 2px ${highlight}`,
                  borderRadius: '2px',
                  backgroundColor: colorHex,
                  filter: 'brightness(0.85)',
                }}
              />
            ))}
          </div>
        );

      case 'vertical-glass-strip':
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'flex',
              gap: '6%',
            }}
          >
            <div
              style={{
                flex: '0 0 20%',
                borderRadius: '3px',
                overflow: 'hidden',
                position: 'relative',
                ...(hasGlass ? {} : raisedPanel),
              }}
            >
              {hasGlass && (
                <div style={{ position: 'absolute', inset: 0 }}>
                  <GlassPane glassId={glassId} />
                </div>
              )}
            </div>
            <div style={{ flex: 1, ...raisedPanel }} />
          </div>
        );

      case 'porthole':
        return (
          <div style={{ position: 'absolute', inset: '8%' }}>
            <div
              style={{
                position: 'absolute',
                top: '12%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '42%',
                paddingBottom: '42%',
                borderRadius: '50%',
                overflow: 'hidden',
                border: `3px solid ${frameAlpha}`,
                backgroundColor: colorHex,
              }}
            >
              {hasGlass && (
                <div style={{ position: 'absolute', inset: 0 }}>
                  <GlassPane glassId={glassId} />
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div
            style={{
              position: 'absolute',
              inset: '8%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: '1fr 1fr',
              gap: '8%',
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={{ ...raisedPanel }} />
            ))}
          </div>
        );
    }
  };

  return (
    <div style={doorWrap}>
      {renderLayout()}
      <Handle />
      <Letterbox />
    </div>
  );
}
